import './promisePolyfill';
import React, { useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { LinearLoadingIndicator, Alert } from '@storebrand-digital/pretzel';
import { getText } from '@storebrand-digital/fms-translations';
import { Document, Page, pdfjs } from 'react-pdf';
import { AxiosError } from 'axios';
import { getDatadogLogger } from '@storebrand-digital/common-datadog';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { useNavigate } from 'react-router-dom';
import styles from './PdfRender.module.css';

interface Props {
  isLoading: boolean;
  error: AxiosError | null;
  pdfBlob: Blob | undefined;
  pageWidth: number | undefined;
}

/**
 * A lot of users have old browsers that new pdfjs-dist does not support
 * TypeError: Promise.withResolvers is not a function. (In 'Promise.withResolvers()', 'Promise.withResolvers' is undefined)
 * Solution: Use legacy pdfjs-dist and polyfill function
 */
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/legacy/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

export const PdfRender = ({
  isLoading,
  error,
  pdfBlob,
  pageWidth,
}: Props) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [hasReachedEnd, setHasReachedEnd] = useState<boolean>(false);
  const [lastPageRendered, setLastPageRendered] = useState<boolean>(false);
  const navigate = useNavigate();
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!lastPageRendered || hasReachedEnd || !sentinelRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasReachedEnd(true);
        }
      },
      { threshold: 1.0 }
    );

    observer.observe(sentinelRef.current);

    return () => observer.disconnect();
  }, [lastPageRendered, hasReachedEnd]);

  useEffect(() => {
    setHasReachedEnd(false);
    setLastPageRendered(false);
  }, [pdfBlob]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  useEffect(() => {
    if (error?.status === 404) {
      getDatadogLogger().error(
        'Document not found. Redirecting to home page.',
        {},
        error
      );
      navigate('/');
    }
  }, [error, navigate]);

  if (isLoading) {
    return (
      <div className={styles.loadingWrapper}>
        <LinearLoadingIndicator type="indeterminate" />
      </div>
    );
  }

  const fetchPDFErrorFMSKey = 'error.preview.fetchPDF';
  const pdfErrorMessage = (
    <div className={styles.errorWrapper}>{getText(fetchPDFErrorFMSKey)}</div>
  );

  if (error) {
    return (
      <div className={styles.errorWrapper}>
        <Alert
          variant="error"
          heading={getText('error.preview.fetchPDF.title')}
          fluid
        >
          {getText(fetchPDFErrorFMSKey)}
        </Alert>
      </div>
    );
  }

  return (
    <ErrorBoundary
      fallback={
        <div className={styles.errorWrapper}>
          <Alert
            variant="error"
            heading={getText('error.preview.fetchPDF.title')}
            fluid
          >
            {getText(fetchPDFErrorFMSKey)}
          </Alert>
        </div>
      }
      onError={(err) =>
        getDatadogLogger().error(
          `Error when rendering pdf: ${err.message}`,
          {},
          err
        )
      }
    >
      <Document
          file={pdfBlob}
          loading={
            <div className={styles.loadingWrapper}>
              <LinearLoadingIndicator type="indeterminate" />
            </div>
          }
          noData={pdfErrorMessage}
          error={pdfErrorMessage}
          onLoadSuccess={onDocumentLoadSuccess}
        >
        {Array.from(Array.from({ length: numPages }), (_, index) => {
          const isLastPage = index === numPages - 1;
          return (
            <Page
              key={`page_${index + 1}`}
              pageNumber={index + 1}
              width={pageWidth ? pageWidth : 1}
              onRenderSuccess={
                isLastPage ? () => setLastPageRendered(true) : undefined
              }
            />
          );
        })}
      </Document>
      <div ref={sentinelRef} style={{ height: 1 }} />
    </ErrorBoundary>
  );
};
